import type React from 'react';

export interface DropdownOption {
  id: string | number;
  label: string;
  depth?: number;
  childCount?: number;
  isLinkable?: boolean;
  /** Icon element to display alongside the label. */
  icon?: React.ReactNode;
  /** Which side of the label the icon appears on. Defaults to 'left'. */
  iconAlign?: 'left' | 'right';
}

/** Props injected into a custom display slot component */
export interface DisplaySlotProps {
  selectedOptions: DropdownOption[];
  isOpen: boolean;
  onToggle: () => void;
  /** Remove a single option from the selection (multi-select only) */
  onClearOption: (id: DropdownOption['id']) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  multiple?: boolean;
  /** Forwarded ref from react-hook-form so the field can be focused on validation error */
  inputRef?: React.Ref<HTMLButtonElement>;
  /** Stable id for the trigger button — tie the <label> htmlFor to this */
  triggerId: string;
  /** Stable id of the listbox — used in aria-controls */
  listboxId: string;
  /** aria-activedescendant value (the id of the currently keyboard-focused option) */
  focusedOptionId?: string;
  /** Keyboard handler — must be forwarded to the trigger button */
  onKeyDown: React.KeyboardEventHandler<HTMLButtonElement>;
  /** Remove the border from the trigger button */
  disableBorder?: boolean;
  /** Compact mode: fixed 121px trigger with no border, shows count badge instead of chips */
  compact?: boolean;
}

/** Props injected into a custom list slot component */
export interface ListSlotProps {
  options: DropdownOption[];
  selectedIds: Array<DropdownOption['id']>;
  onSelect: (option: DropdownOption) => void;
  onClose: () => void;
  multiple?: boolean;
  /** Index of the keyboard-focused option (for visual highlight and aria-activedescendant sync) */
  focusedIndex: number;
  /** The listbox element must have this id so aria-controls resolves correctly */
  listboxId: string;
  /** Prefix used to generate per-option ids: `${optionIdPrefix}-${index}` */
  optionIdPrefix: string;
  /** Current inset search query, if any. List components can use this to auto-expand matching ancestors. */
  insetQuery?: string;
}

/**
 * Filters an options array to only those whose label contains the query string,
 * plus all ancestor nodes required to keep the tree structure intact.
 * Returns the full array unmodified when query is empty.
 */
export function filterOptionsWithAncestors(options: DropdownOption[], query: string): DropdownOption[] {
  if (!query.trim()) return options;
  const q = query.toLowerCase();

  const matchingIndices = new Set<number>();
  options.forEach((o, i) => {
    if (o.label.toLowerCase().includes(q)) matchingIndices.add(i);
  });
  if (matchingIndices.size === 0) return [];

  const includeIndices = new Set<number>(matchingIndices);
  matchingIndices.forEach((idx) => {
    let currentDepth = options[idx].depth ?? 0;
    for (let i = idx - 1; i >= 0 && currentDepth > 0; i--) {
      const ancestorDepth = options[i].depth ?? 0;
      if (ancestorDepth < currentDepth) {
        includeIndices.add(i);
        currentDepth = ancestorDepth;
      }
    }
  });

  return options.filter((_, i) => includeIndices.has(i));
}

/**
 * Marks a list slot component as requiring a minimum number of selections.
 * Attach as a static property: `(MyList as typeof MyList & WithMinSelections).minSelections = 1`.
 */
export interface WithMinSelections {
  minSelections?: number;
}
